import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import {
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Download,
  Printer,
  CopyCheck,
} from "lucide-react";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric } from "validator";
import { getRcProDetails } from "../../../Services/ApiTestService/apiTestService";
import * as XLSX from "xlsx"; // Added XLSX import

function RcPro() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const data = response?.data;

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const [isFormattedCopied, setIsFormattedCopied] = useState(false); // New state for formatted copy
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;
    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("JSON Copied!"));

    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  // 1. Copy Formatted Data
  const handleCopyFormatted = () => {
    if (!data) return;
    const text = `
Registration No: ${data.regNo || "-"}

 Vehicle Overview
Registration: ${data.regNo || "-"}
Owner: ${data.owner || "-"}
Status: ${data.status || "-"}
Registration Date: ${data.regDate || "-"}
RC Expiry: ${data.rcExpiryDate || "-"}
Owner Count: ${data.ownerCount || "-"}

 Vehicle Details
Manufacturer: ${data.vehicleManufacturerName || "-"}
Model: ${data.model || "-"}
Fuel: ${data.type || "-"}
Colour: ${data.vehicleColour || "-"}
Vehicle Class: ${data.vehicleClass || "-"}
Category: ${data.vehicleCategory || "-"}
Seats: ${data.vehicleSeatCapacity || "-"}
Cylinders: ${data.vehicleCylindersNo || "-"}
Engine CC: ${data.vehicleCubicCapacity ? data.vehicleCubicCapacity + " cc" : "-"}

 Registration
RTO: ${data.regAuthority || "-"}
RTO Code: ${data.rtoCode || "-"}
Norms: ${data.normsType || "-"}
Body Type: ${data.bodyType || "-"}
Manufacturing: ${data.vehicleManufacturingMonthYear || "-"}

 Insurance & PUC
Insurance Company: ${data.vehicleInsuranceCompanyName || "-"}
Insurance Valid: ${data.vehicleInsuranceUpto || "-"}
Policy Number: ${data.vehicleInsurancePolicyNumber || "-"}
PUCC Number: ${data.puccNumber || "-"}
PUCC Valid: ${data.puccUpto || "-"}

 Technical
Gross Weight: ${data.grossVehicleWeight ? data.grossVehicleWeight + " kg" : "-"}
Unladen Weight: ${data.unladenWeight ? data.unladenWeight + " kg" : "-"}
Wheelbase: ${data.wheelbase ? data.wheelbase + " mm" : "-"}
Commercial: ${data.isCommercial ? "Yes" : "No"}
Financed: ${data.financed ? "Yes" : "No"}

 Address
Present: ${data.presentAddress || "-"}
Permanent: ${data.permanentAddress || "-"}
    `.trim();

    navigator.clipboard.writeText(text).then(() => {
      showSuccessToast("Formatted data copied!");
      setIsFormattedCopied(true);
      setTimeout(() => setIsFormattedCopied(false), 2000);
    });
  };

  // 2. Export to Excel
  const handleExportExcel = () => {
    if (!data) return;
    const excelData = [
      { Field: "Registration", Value: data.regNo },
      { Field: "Owner", Value: data.owner },
      { Field: "Status", Value: data.status },
      { Field: "Registration Date", Value: data.regDate },
      { Field: "RC Expiry", Value: data.rcExpiryDate },
      { Field: "Owner Count", Value: data.ownerCount },
      { Field: "Manufacturer", Value: data.vehicleManufacturerName },
      { Field: "Model", Value: data.model },
      { Field: "Fuel", Value: data.type },
      { Field: "Colour", Value: data.vehicleColour },
      { Field: "Vehicle Class", Value: data.vehicleClass },
      { Field: "Category", Value: data.vehicleCategory },
      { Field: "Seats", Value: data.vehicleSeatCapacity },
      { Field: "Cylinders", Value: data.vehicleCylindersNo },
      { Field: "Engine CC", Value: data.vehicleCubicCapacity },
      { Field: "RTO", Value: data.regAuthority },
      { Field: "RTO Code", Value: data.rtoCode },
      { Field: "Norms", Value: data.normsType },
      { Field: "Body Type", Value: data.bodyType },
      { Field: "Manufacturing", Value: data.vehicleManufacturingMonthYear },
      { Field: "Insurance Company", Value: data.vehicleInsuranceCompanyName },
      { Field: "Insurance Valid", Value: data.vehicleInsuranceUpto },
      { Field: "Policy Number", Value: data.vehicleInsurancePolicyNumber },
      { Field: "PUCC Number", Value: data.puccNumber },
      { Field: "PUCC Valid", Value: data.puccUpto },
      { Field: "Gross Weight", Value: data.grossVehicleWeight },
      { Field: "Unladen Weight", Value: data.unladenWeight },
      { Field: "Wheelbase", Value: data.wheelbase },
      { Field: "Commercial", Value: data.isCommercial ? "Yes" : "No" },
      { Field: "Financed", Value: data.financed ? "Yes" : "No" },
      { Field: "Present", Value: data.presentAddress },
      { Field: "Permanent", Value: data.permanentAddress },
    ];

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RC Details");
    XLSX.writeFile(workbook, `RC_Details_${data.regNo || "Export"}.xlsx`);
  };

  // 3. Print Function
  const handlePrint = () => {
    window.print();
  };

  const handleRcPro = async () => {
    if (!profileData) return;

    const validate = isAlpha(vehicleNumber);

    if (!validate) {
      return setInputError(
        "⚠️ Please enter alpha numeric in vehicle No. field",
      );
    } else if (vehicleNumber.length < 7) {
      return setInputError("⚠️ Enter at least 7 digits in vehicle No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getRcProDetails({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_num: vehicleNumber.trim(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <div className="print:hidden">
        <Seo />
        <Header heading={serviceName} />
      </div>

      {isLoading && (
        <div className="flex justify-center mt-6 print:hidden">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 print:pb-0">
        {/* ---------------- FORM (Hidden on print) ---------------- */}
        <div className="max-w-3xl border rounded-md sm:p-6 mt-6 bg-white print:hidden">
          <div className="flex items-center flex-col flex-wrap gap-4">
            {/* Vehicle */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Vehicle No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: GJ08BN1256"
              />
            </div>

            {/* show input errors */}
            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end">
              <button
                disabled={isLoading || !vehicleNumber}
                onClick={handleRcPro}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {/* response message (Hidden on print) */}
        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold print:hidden`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions (Hidden on print) */}
        <div className="w-full flex justify-center print:hidden">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          // Adjusted grid classes to fall back to a single column block when printing
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-1 gap-6 mt-10 w-full max-w-6xl print:mt-0 print:block">
            {/* -------- JSON VIEW (Hidden on print) -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto print:hidden">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? (
                  <CopyCheck className="text-green-400" />
                ) : (
                  <Copy />
                )}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8 border-gray-600" />
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- FORMATTED VIEW -------- */}
            {data && (
              <div className="bg-white rounded-2xl shadow-xl border overflow-hidden print:shadow-none print:border-none print:w-full">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-6 relative">
                  <h2 className="text-3xl font-bold flex items-center gap-3">
                    <ListCheck size={32} />
                    Full Vehicle Details
                  </h2>
                  <p className="opacity-90 mt-1">
                    Registration No : <b>{data.regNo}</b>
                  </p>

                  {/* Actions Bar (Hidden on print) */}
                  <div className="absolute top-6 right-6 flex items-center gap-3 print:hidden">
                    <button
                      onClick={handleCopyFormatted}
                      title="Copy Formatted Text"
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
                    >
                      {isFormattedCopied ? (
                        <CopyCheck size={20} />
                      ) : (
                        <Copy size={20} />
                      )}
                    </button>
                    <button
                      onClick={handleExportExcel}
                      title="Export to Excel"
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
                    >
                      <Download size={20} />
                    </button>
                    <button
                      onClick={handlePrint}
                      title="Print Document"
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
                    >
                      <Printer size={20} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 p-6">
                  {/* Vehicle Overview */}
                  <Section
                    title="Vehicle Overview"
                    fields={[
                      ["Registration", data.regNo],
                      ["Owner", data.owner],
                      ["Status", data.status],
                      ["Registration Date", data.regDate],
                      ["RC Expiry", data.rcExpiryDate],
                      ["Owner Count", data.ownerCount],
                    ]}
                  />

                  {/* Vehicle Details */}
                  <Section
                    title="Vehicle Details"
                    fields={[
                      ["Manufacturer", data.vehicleManufacturerName],
                      ["Model", data.model],
                      ["Fuel", data.type],
                      ["Colour", data.vehicleColour],
                      ["Vehicle Class", data.vehicleClass],
                      ["Category", data.vehicleCategory],
                      ["Seats", data.vehicleSeatCapacity],
                      ["Cylinders", data.vehicleCylindersNo],
                      [
                        "Engine CC",
                        data.vehicleCubicCapacity
                          ? data.vehicleCubicCapacity + " cc"
                          : "",
                      ],
                    ]}
                  />

                  {/* Registration */}
                  <Section
                    title="📑 Registration"
                    fields={[
                      ["RTO", data.regAuthority],
                      ["RTO Code", data.rtoCode],
                      ["Norms", data.normsType],
                      ["Body Type", data.bodyType],
                      ["Manufacturing", data.vehicleManufacturingMonthYear],
                    ]}
                  />

                  {/* Insurance */}
                  <Section
                    title="🛡 Insurance & PUC"
                    fields={[
                      ["Insurance Company", data.vehicleInsuranceCompanyName],
                      ["Insurance Valid", data.vehicleInsuranceUpto],
                      ["Policy Number", data.vehicleInsurancePolicyNumber],
                      ["PUCC Number", data.puccNumber],
                      ["PUCC Valid", data.puccUpto],
                    ]}
                  />

                  {/* Technical */}
                  <Section
                    title="⚙ Technical"
                    fields={[
                      [
                        "Gross Weight",
                        data.grossVehicleWeight
                          ? data.grossVehicleWeight + " kg"
                          : "",
                      ],
                      [
                        "Unladen Weight",
                        data.unladenWeight ? data.unladenWeight + " kg" : "",
                      ],
                      [
                        "Wheelbase",
                        data.wheelbase ? data.wheelbase + " mm" : "",
                      ],
                      ["Commercial", data.isCommercial ? "Yes" : "No"],
                      ["Financed", data.financed ? "Yes" : "No"],
                    ]}
                  />

                  {/* Address */}
                  <Section
                    title="🏠 Address"
                    fields={[
                      ["Present", data.presentAddress],
                      ["Permanent", data.permanentAddress],
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default RcPro;

const Section = ({ title, fields }) => (
  <div className="rounded-xl border bg-gray-50 print:break-inside-avoid print:border-gray-300">
    <div className="bg-blue-50 border-b px-5 py-3 print:bg-gray-100">
      <h3 className="font-semibold text-blue-700 text-lg print:text-black">
        {title}
      </h3>
    </div>

    <div className="divide-y print:divide-gray-300">
      {fields.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4 px-5 py-3">
          <span className="text-gray-500 print:text-gray-700">{label}</span>
          <span className="font-semibold text-right break-all print:text-black">
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  </div>
);
